import * as THREE from '@phdsoft/three';
import { DomainEntity } from "../DomainEntity";
import { DocumentResource } from "../ResourceElements/document";
import { MaterialType } from "../ResourceElements/MaterialType";
import { WorkableElement } from '../SectionElements/WorkableElement';
import { Section } from "./Section";
import { SectionBorderSettings } from './section-border-settings';
import { StructureDegradationParameters } from 'src/app/degradation/models/degradation-curve';
import { ELEMENTSTYPESIDS } from 'projects/phdsoft/common/src/lib/shared/constants/elementsTypesIds';

export interface ElementType {
  name: string;
  id: string;
}


export class Region extends DomainEntity {

  private _group: THREE.Group;
  public get group(): THREE.Group {
    return this._group;
  }
  public set group(v: THREE.Group) {
    this._group = v;
  }

  private _cssGroup: THREE.Group;
  public get cssGroup(): THREE.Group {
    return this._cssGroup;
  }
  public set cssGroup(v: THREE.Group) {
    this._cssGroup = v;
  }

  public get visible(): boolean {
    return this._group.visible /*&& this._group.parent.visible*/;
  }
  public set visible(v: boolean) {
    this._group.visible = v;
  }

  public set visibleAllChild(v: boolean) {
    this._group.visible = v;
    for (const section of this.sections) {
      section.visible = v;
      section.GroupsVisibility = v;
    }
  }

  /** Toggle visibility of section-level border and lightSection groups (REGIONS, new format; borders also gated by the "show section borders" setting) */
  public set visibleSectionLayer(v: boolean) {
    for (const section of this.sections) {
      section.visible = v;
      // was: if (section.borderGroup) section.borderGroup.visible = v;
      if (section.borderGroup) section.borderGroup.visible = v && SectionBorderSettings.show;
      if (section.lightSectionGroup) section.lightSectionGroup.visible = v;
    }
  }

  /** Get all lightSectionGroup groups from sections */
  public get LightSectionsGroups(): Array<THREE.Group> {
    return this.sections
      .map(s => s.lightSectionGroup)
      .filter(g => g != null);
  }

  /** MODEL mode: show lightSection only for all sections */
  public setViewOnModelMode(): void {
    for (const section of this.sections) {
      section.setViewOnModelMode();
    }
  }

  /** REGIONS mode: show border + lightSection, hide surface/linear for all sections */
  public setViewOnRegionMode(): void {
    for (const section of this.sections) {
      section.setViewOnRegionMode();
    }
  }

  /** New format, MODEL/REGIONS: Borders group per the "show section borders" setting, nothing else touched */
  public applySectionBorderSetting(): void {
    for (const section of this.sections) {
      section.applySectionBorderSetting();
    }
  }

  /** Structure view mode: show surface/linear/border, hide lightSection */
  public setViewOnModelModeForStructureViewMode(): void {
    for (const section of this.sections) {
      section.setViewOnSectionMode();
    }
  }

  private _selected: boolean;
  public get selected(): boolean {
    return this._selected;
  }
  public set selected(v: boolean) {
    this._selected = v;
  }

  private _regionsDocuments: Array<DocumentResource> = new Array<DocumentResource>();
  public get regionsDocuments(): Array<DocumentResource> {
    return this._regionsDocuments;
  }
  public set regionsDocuments(v: Array<DocumentResource>) {
    this._regionsDocuments = v;
  }

  private _sections: Array<Section> = new Array<Section>();
  public get sections(): Array<Section> {
    return this._sections;
  }
  public set sections(v: Array<Section>) {
    this._sections = v;
  }

  public get ElementsCount(): number {
    return this.sections.map(x => x.ElementsCount).reduce((a, b) => a + b);
  }

  public get Elements(): Array<WorkableElement> {
      return this.sections.flatMap(x => [x.surfaceElements, x.linearElements, this.surfaceElements].flatMap(y => y));
  }

  public get SurfaceElements(): Array<WorkableElement> {
    return this.sections.flatMap(x => x.surfaceElements);
  }

  public get LinearElements(): Array<WorkableElement> {
    return this.sections.flatMap(x => x.linearElements);
  }

  private _isStructural: boolean = false;
  public get isStructural(): boolean {
    return this._isStructural;
  }
  public set isStructural(v: boolean) {
    this._isStructural = v;
  }

  private _isLnesRenderer: boolean;
  public get isLnesRenderer(): boolean {
    return this._isLnesRenderer;
  }
  public set isLnesRenderer(v: boolean) {
    this._isLnesRenderer = v;
  }

  private _surfaceElements: Array<WorkableElement> = new Array<WorkableElement>();
  public get surfaceElements(): Array<WorkableElement> {
    return this._surfaceElements;
  }
  public set surfaceElements(v: Array<WorkableElement>) {
    this._surfaceElements = v;
  }

  private _isPropertiesLoaded: boolean = false;
  public get isPropertiesLoaded(): boolean {
    return this._isPropertiesLoaded;
  }
  public set isPropertiesLoaded(v: boolean) {
    this._isPropertiesLoaded = v;
  }


  constructor(regionId: string, regionName: string) {
    super();
    this.Guid = regionId;
    this.Name = regionName;
    this._cssGroup = new THREE.Group();
    this._group = new THREE.Group();
    this._group.name = this.Guid;
  }

  public renderLinearElements(value: boolean): void {
    for (const section of this.sections) {
      if (section.surfaceGroup.visible)
        section.linearGroup.visible = value;
    }
  }

  public renderBorderofElements(material: THREE.Material): void {
    this.isLnesRenderer = true;
    for (const section of this.sections) {
      if (section.surfaceGroup.visible)
        section.renderBorders(material);
    }
  }

  public clearBorderofElements(): void {
    this.isLnesRenderer = false;
    for (const section of this.sections) {
      section.clearBorders();
    }
  }

  public set sectionsVisibility(v: boolean) {
    for (const section of this.sections) {
      section.group.visible = v;
      section.surfaceGroup.visible = v;
      section.linearGroup.visible = false;
    }
  }

  public changeSectionVisibility(sectionId: string) {
    var section = this.sections.find(x => x.Guid === sectionId);
    if (section) section.GroupsVisibility = !section.visible;
  }

  public changeSectionVisibilityModelMode(sectionId: string) {
    var section = this.sections.find(x => x.Guid === sectionId);
    if (section) {
      section.visible = !section.visible;
      section.surfaceGroup.visible = section.visible
    }
  }

  public setElementsProperties(elementsProperties: any, materials: Array<MaterialType>): void {
    this._isPropertiesLoaded = true;
    for (const section of this.sections) {
      const elementsPerSection = elementsProperties.filter(x => x.SectionId === section.Guid);
      section.setElementsProperties(elementsPerSection, materials);
    }
  }

  public clearElementsProperties(): void {
    for (const section of this.sections) {
      section.clearElementsProperties();
    }
  }

  public addSurfaceElement(element: WorkableElement): void {
    const section = this.sections.find(x => x.Guid === element.SectionId);
    if (section) section.addSurfaceElement(element);
    else {
      const newSection: Section = new Section(element.SectionId, element.SectionName);
      newSection.addSurfaceElement(element);
      this.addSection(newSection);
    }
  }

  public addLinearElement(element: WorkableElement, target: WorkableElement = null): void {
    const section = this.sections.find(x => x.Guid === element.SectionId);
    if (section) section.addLinearElement(element, target);
    else {
      const newSection: Section = new Section(element.SectionId, element.SectionName);
      newSection.linearGroup.visible = true;
      newSection.addLinearElement(element, target);
      this.addSection(newSection);
    }
  }

  public addSurfaceCSSElement(element: WorkableElement): void {
    const section = this.sections.find(x => x.Guid === element.SectionId);
    if (section) section.addSurfaceCSSElement(element);
  }


  public addSection(section: Section): void {
    this.group.add(section.group);
    this.sections.push(section);
  }

  public getElementById(sectionId: string, id: string): WorkableElement {
    const section = this.sections.find(x => x.Guid === sectionId);
    if (section) return section.getElementById(id);
    return null;
  }

  /** Detaches (without disposing) the first instance with this id from any of the sections. */
  public detachElementByIdOnly(id: string): WorkableElement {
    for (const section of this.sections) {
      const element = section?.detachElementByIdOnly(id);
      if (element) return element;
    }
    return null;
  }

  public deleteElementByIdOnly(id: string): void {
    for (const section of this.sections) {
      if (section) section.deleteElementByIdOnly(id);
    }
    return null;
  }

  public deleteLinearElementByIdOnly(id: string): void {
    for (const section of this.sections) {
      if (section) section.deleteLinearElementByIdOnly(id);
    }
    return null;
  }

  public getElementByIdOnly(id: string): WorkableElement {
    for (const section of this.sections) {
      if (section) {
        const workableElement = section.getElementById(id);
        if (workableElement) return workableElement;
      }
    }
    return null;
  }

  public getVisibleBoundingBox(): THREE.Box3 {
    const box = new THREE.Box3();
    this._group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        box.expandByObject(child);
      }
    });
    return box;
  }

  public updateDegradationColors(degradationCurves: StructureDegradationParameters[]): void {
    for (const section of this.sections) {
      section.updateDegradationColors(degradationCurves);
    }
  }

  public updateRemainingLifeViewMode(low: number, high: number, degradationCurves: StructureDegradationParameters[]): void {
    for (const section of this.sections) {
      section.updateRemainingLifeViewMode(low, high, degradationCurves);
    }
  }

  public updateCorrosionRateViewMode(low: number, high: number, degradationCurves: StructureDegradationParameters[]): void {
    for (const section of this.sections) {
      section.updateCorrosionRateViewMode(low, high, degradationCurves);
    }
  }

  public updateCorrosionRiskIndexViewMode(low: number, high: number, degradationCurves: StructureDegradationParameters[]): void {
    for (const section of this.sections) {
      section.updateCorrosionRiskIndexViewMode(low, high, degradationCurves);
    }
  }

  public getVisibleElementTypes(): Array<ElementType> {
    let types: Set<string> = new Set();
    return this.SurfaceElements.concat(this.LinearElements).map((element) => {
      if(
        element.realVisibility && 
        element.TypeId && 
        !types.has(element.TypeId.toString())
      ) {
        types.add(element.TypeId.toString());
        return {
          name: ELEMENTSTYPESIDS.getElementTypeName(element.TypeId.toString()),
          id: element.TypeId.toString()
        };
      }
    }).filter(type => type !== undefined);
  }

  public getRegionElementsTypes(): Array<ElementType> {
    let types: Set<string> = new Set();
    return this.SurfaceElements.concat(this.LinearElements).map((element) => {
      if(
        element.TypeId && 
        !types.has(element.TypeId.toString())
      ) {
        types.add(element.TypeId.toString());
        return {
          name: ELEMENTSTYPESIDS.getElementTypeName(element.TypeId.toString()),
          id: element.TypeId.toString()
        };
      }
    }).filter(type => type !== undefined);
  }


  private static readonly STRUCTURAL_TYPE_IDS = new Set([
    ELEMENTSTYPESIDS.PLATE, ELEMENTSTYPESIDS.STIFFENER, ELEMENTSTYPESIDS.FACEPLATE,
    ELEMENTSTYPESIDS.BRACKET, ELEMENTSTYPESIDS.BEAM, ELEMENTSTYPESIDS.BEAMPART,
    ELEMENTSTYPESIDS.CABLE, ELEMENTSTYPESIDS.PIPE, ELEMENTSTYPESIDS.CONCRETE,
  ]);

  public getVisibleElementTypesFromSections(): string[] {
    const typesSet = new Set<string>();
    const elements = this.SurfaceElements.concat(this.LinearElements);
    for (const element of elements) {
      if (!element.section?.group?.visible) continue;
      const typeId = element.TypeId?.toString();
      if (!typeId || !Region.STRUCTURAL_TYPE_IDS.has(typeId)) continue;
      const typeName = ELEMENTSTYPESIDS.getElementTypeName(typeId);
      if (typeName) typesSet.add(typeName);
    }
    return Array.from(typesSet);
  }

  public getStructureVisibleElementsTypes(): Array<string> {
    // use Set to speed up type checking
    let typesSet: Set<string> = new Set();
    let structureElements = this.SurfaceElements.concat(this.LinearElements);
    let elementsTypes: Array<string> = structureElements.reduce((typesArray, element) => {
      if (!element.realVisibility || (element.properties?.TypeName == null || typesSet.has(element.properties?.TypeName))) {
        return typesArray;
      }
      typesSet.add(element.properties.TypeName);
      typesArray.push(element.properties.TypeName);
      return typesArray;
    }, [])

    return elementsTypes;
  }

  public getProtectionElementsVisibleElementsTypes(): Array<string> {
    // use Set to speed up type checking
    let typesSet: Set<string> = new Set();
    let protectionElements = this.SurfaceElements.concat(this.LinearElements);
    let elementsTypes: Array<string> = protectionElements.reduce((typesArray, element) => {
      if (!element.realVisibility || (element.properties?.TypeName == null || typesSet.has(element.properties?.TypeName))) {
        return typesArray;
      }
      typesSet.add(element.properties.TypeName);
      typesArray.push(element.properties.TypeName);
      return typesArray;
    }, [])

    return elementsTypes;
  }

  public getStructureElementsTypes(): Array<string> {
    // use Set to speed up type checking
    let typesSet: Set<string> = new Set();
    let structureElements = this.SurfaceElements.concat(this.LinearElements);

    let elementsTypes: Array<string> = structureElements.reduce((typesArray, element) => {
      if (element.properties?.TypeName == null || typesSet.has(element.properties?.TypeName)) {
        return typesArray;
      }
      typesSet.add(element.properties.TypeName);
      typesArray.push(element.properties.TypeName);
      return typesArray;
    }, [])

    return elementsTypes;
  }

  public setBackToDefaultMaterial(): void {
    for (const surfaceElement of this.SurfaceElements) {
      surfaceElement.currentRenderMaterial = surfaceElement.currentMaterial;
    }
    for (const surfaceElement of this.LinearElements) {
      surfaceElement.currentRenderMaterial = surfaceElement.currentMaterial;
    }
  }
}
